/**
 * What colour a tracer is, and whose it is.
 *
 * Split out of `three/tracer-pool.ts` and `tracer-bolts.ts` when the side
 * tinting landed, because it is neither of their jobs: the pool is scene-graph
 * work and the bolts are geometry, while this is a palette and one piece of
 * arithmetic over it. Pure — no three.js — so the derivation is testable
 * without a GPU, which is the same reason `tracer-bolts.ts` is pure.
 *
 * `docs/engineering/enhanced-tracers.md` is the long-form record; the essential
 * parts are repeated here because a reader of the shader should not have to
 * leave the source to find out whether these colours were recovered or invented.
 *
 * ## The tinting is a port enhancement, and it is invented
 *
 * **The original draws one projectile colour for everyone.** `BULLETS.BMP` is a
 * single 808-byte sheet whose thirteen frames vary in *size and orientation
 * only*, all inked in the same near-black-to-grey (body 20/255, brightest
 * fringe 121/255) over a binary alpha. The per-aircraft gun records carry
 * position and count and no colour field. The decoded `MANUAL.MVB` mentions
 * tracers only as historical prose and as gunnery advice ("Squeeze off a short
 * burst, and watch the tracers"), and names incendiary rounds as a *load* for
 * balloons without ever claiming they look different. The first tinting lane
 * went looking for evidence that the game distinguished sides and found none.
 *
 * The history is real but it is not the warrant: WW1 tracer composition did
 * vary by nation — the British Buckingham and .303 SPG rounds against the
 * German phosphorus loads — and observers did report differently-coloured
 * streams. That is a pleasant coincidence, not a recovered fact. These colours
 * were chosen so a pilot can tell friend from foe in a melee.
 *
 * The authentic pixel path is untouched and never reads a side
 * (`bullet-sprites.test.ts`'s "the pixel path is blind to the shooter's side").
 *
 * ## 2026-08-25: the subtle version was flown, and it did not work
 *
 * The tinting shipped on 2026-08-24 held all three tiers to **one luminance** and
 * separated them by moving one rung along `BARON.PAL`'s fire ramp — 210 / 212 /
 * 213, three near neighbours. Its own swapchain census concluded that the
 * luminance spread across tiers was 0.1–1.6 of 255, i.e. that *legibility did
 * not encode side*, and treated that as the success condition. The user flew
 * against it and reported the failure the census could not see: **"enemy bullets
 * are not visually distinguishable from player bullets/tracers"** (2026-08-25).
 *
 * Two mechanisms, and the second is the larger:
 *
 * 1. **The rungs were adjacent.** Composited over a noon sky, the friendly and
 *    hostile head glows measured **CIELAB dE76 2.4** apart — one unit above the
 *    threshold two flat patches need side by side in a lab, for two moving
 *    streaks in a turning fight.
 * 2. **The shader gave the tint almost no weight.** The head mixed
 *    `tint -> TRACER_HOT_COLOUR` by the *falloff*, so the tint was strongest
 *    exactly where the falloff had taken the intensity to nothing. Integrated
 *    over the quad the tint carried **30 %** of the head's emitted light and the
 *    shared white carried 70 %; the tail was 23 % against 57 %. A hue moved on a
 *    rung that owns a quarter of the picture is a hue moved a quarter as far.
 *
 * And the equal-luminance derivation, meant as a safeguard, was actively
 * harmful for one group of players: **a deuteranope discriminates warm hues
 * almost entirely by luminance**, so holding all three to one luminance removed
 * the only channel he had. Simulated (Viénot–Brettel–Mollon), the shipped
 * friendly and hostile streams were **dE76 0.0–0.4** apart — not subtle, *the
 * same colour*, for the ~5 % of male players with a deuteran deficiency.
 *
 * So this revision spends its budget the other way round: hue **and** luminance
 * move together, and they move only between the sides.
 */

import type { RoundView } from "./encounter-setup.ts";

/**
 * Who fired, at the only resolution a tracer's colour cares about.
 *
 * Three tiers rather than two because the player asked for his *own* fire to be
 * distinguishable from his flight's (user, 2026-08-24: "and the player") — a
 * wingman's rounds are friendly, but they are not the ones he is aiming, and in
 * a four-ship flight most of the friendly fire on screen is somebody else's.
 *
 * They are not three *equal* distinctions, and after 2026-08-25 they are
 * deliberately not: the user names **two factions**, so friend-versus-foe is the
 * loud one and player-versus-wingman is a shade within the friendly family. See
 * `TRACER_MID_PLAYER`.
 */
export const TRACER_TIERS = ["player", "friendly", "hostile"] as const;

export type TracerTier = (typeof TRACER_TIERS)[number];

/**
 * Which tier a round belongs to.
 *
 * `fromPlayer` first, because it is the narrower question and the answer that
 * outranks the other: the player is on the friendly side, so asking about the
 * side first would swallow his own stream into his flight's.
 *
 * `"friendly"` is taken to be the player's side, which is the engine's own
 * convention rather than an assumption made here — `EncounterStatus` spells its
 * outcomes `"playerDown"` and `"enemyDown"` off exactly these two labels, and
 * every setup in `encounter-setup.ts` and every generated mission puts the
 * player's flight on `"friendly"`.
 */
export function tracerTierFor(round: RoundView): TracerTier {
  if (round.fromPlayer) return "player";
  return round.shooterSide === "friendly" ? "friendly" : "hostile";
}

/**
 * `BARON.PAL` 209 `#ffffe7` — the white-yellow the original's own fire ramp
 * starts at (`fire-palette.ts`'s census of `EXPLO.BMP`). The hottest colour
 * this game has, which is what a tracer's head should be.
 */
export const TRACER_HOT_COLOUR = 0xffffe7;

/**
 * `BARON.PAL` 211 `#fbfb00` — the reference rung.
 *
 * Two jobs, and it is worth saying which is which. It is the **friendly
 * stream's own colour** (`TRACER_MID_FRIENDLY`), and it is the luminance every
 * other tier's is stated as a fraction of (`tracerTint`). It moved one rung
 * hotter on 2026-08-25, from 212 `#f7c300` amber to this: the user's own words
 * for what he expects are *"orange"* for the enemy and *"yellow tracers for
 * friendlies"* (2026-08-23), and 211 is the ramp's unmixed yellow while 212 is
 * already halfway to the orange the enemy now owns.
 */
export const TRACER_MID_COLOUR = 0xfbfb00;

/** `BARON.PAL` 215 `#eb2800` — six rungs down: the dull red the tail dies at before it fades out. */
export const TRACER_COOL_COLOUR = 0xeb2800;

/**
 * The three sides' **mid** rungs — the one rung of the ramp that changes with
 * who fired.
 *
 * `TRACER_HOT_COLOUR` and `TRACER_COOL_COLOUR` are still **shared by all three
 * tiers**, and that is what keeps this a shift rather than a recolour: every
 * tracer in the sky has the same white-hot core and dies to the same dull red
 * ember, because those are what *burning* looks like at either end whatever is
 * burning. Only the middle of the ramp moves, and it moves along `BARON.PAL`'s
 * own incandescence ladder rather than off it. All three are real palette
 * entries from the same 209-215 run:
 *
 * ```
 * 209 #ffffe7  white-hot   <- TRACER_HOT_COLOUR, shared
 * 210 #fbfb6d  pale yellow <- the player
 * 211 #fbfb00  yellow      <- his flight, and the reference luminance
 * 212 #f7c300  amber
 * 213 #f38e00  orange
 * 214 #ef5d00  deep orange <- the enemy
 * 215 #eb2800  red         <- TRACER_COOL_COLOUR, shared
 * ```
 *
 * **The gap is spent where the user asked for it.** Before 2026-08-25 the three
 * tiers took 210 / 212 / 213 — one rung between each pair, the enemy's distance
 * from his flight's no larger than the player's from his own wingmen's. The
 * report was that the enemy's fire was not distinguishable, so the two friendly
 * streams close up into the yellow family (210 and 211, **adjacent** rungs) and
 * the enemy drops to 214, **three rungs** below the nearer of them and a real
 * orange rather than an amber.
 *
 * **210 rather than 211 for the player** is the one choice inside the friendly
 * family, and the argument for it survives the revision unchanged: 210 is the
 * last rung of the ramp that still has **blue** in it (109) and no rung below it
 * has any, so player-versus-wingman separates on a channel of its own instead of
 * asking the eye to grade two yellows against each other. It reads as *paler*
 * rather than as a different colour, which is the right weight for a distinction
 * the user did not ask for.
 */
export const TRACER_MID_PLAYER = 0xfbfb6d;
/** The reference rung itself — `tracerTint` returns it at a scale of exactly 1. */
export const TRACER_MID_FRIENDLY = TRACER_MID_COLOUR;
export const TRACER_MID_HOSTILE = 0xef5d00;

/**
 * How luminous each tier's mid rung is, as a fraction of `TRACER_MID_COLOUR`'s.
 *
 * ## Why the friendly pair is held equal and the enemy is not
 *
 * The 2026-08-24 tinting renormalised **all three** tiers to one luminance, on
 * the argument that otherwise "legibility encodes side" and a hostile tracer
 * would be the hardest of the three to see at night. Half of that argument is
 * still right and is kept: *within* the friendly family, which stream is whose
 * must not change how findable it is, so `player` and `friendly` are both 1 and
 * differ in chromaticity alone.
 *
 * The other half was wrong twice over, and the revision is why:
 *
 * - **It removed the only channel a colour-deficient player has.** A deuteranope
 *   discriminates yellow from orange from red almost entirely by luminance. Held
 *   to one luminance, the shipped friendly and hostile streams simulate
 *   (Viénot–Brettel–Mollon) at **dE76 0.0–0.4** — indistinguishable, for the
 *   ~5 % of male players with a deuteran deficiency, which is exactly the group
 *   "distinguish factions in a dogfight" fails hardest for. At 0.55 the same
 *   pair simulates at **4.5–15.8**.
 * - **It priced findability wrongly.** Findability is carried by the head's
 *   shared white core, which is `TRACER_HOT_COLOUR` at `TRACER_HEAD_GAIN` for
 *   every tier and saturates, and by the halo — whose falloff is a **cube**.
 *   Cutting the halo's luminance by 45 % therefore costs 10 % of the glow's
 *   apparent area, not 45 %: measured against the visibility threshold of one
 *   VGA DAC step (4/255), the hostile glow's visible radius is 0.771 of the quad
 *   against the friendly's 0.812, i.e. **0.594 against 0.660 in area**. That is
 *   the measurement the old argument asserted around, and
 *   `tracer-material.test.ts` pins it.
 *
 * 0.55 is where the two curves cross: below it the enemy's halo starts to lose
 * apparent size faster than it gains deuteran separation, and above it the
 * simulated separation falls off a cliff (0.75 gives 4–7, 0.85 gives 2, 1.0
 * gives 0.1–0.4 again).
 */
export const TRACER_TIER_LUMA_FRACTION: Readonly<Record<TracerTier, number>> = {
  player: 1,
  friendly: 1,
  hostile: 0.55,
};

/** Rec. 709 luminance of an 8-bit sRGB triple — the coefficients, and the domain, `searchlight-beams.ts` uses. */
function luma(red: number, green: number, blue: number): number {
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

/** `TRACER_MID_COLOUR`'s own Rec. 709 luminance, in bytes — every tier's target is a fraction of this. */
export function referenceLuma(): number {
  return luma((TRACER_MID_COLOUR >> 16) & 0xff, (TRACER_MID_COLOUR >> 8) & 0xff, TRACER_MID_COLOUR & 0xff);
}

/**
 * A palette entry's **chromaticity renormalised to `fraction` of
 * `TRACER_MID_COLOUR`'s Rec. 709 luminance** — the derivation `BEAM_NEAR_COLOUR`
 * is built by (`docs/engineering/searchlights-design.md` §11.7), with a target
 * rather than an equality.
 *
 * Renormalising at all is what stops the *ladder's* own brightness ordering
 * leaking in as an accident: taken raw off the ramp, 214 is 51 % as luminous as
 * 211 for reasons that have nothing to do with which side is meant to read
 * brighter. Stating the fraction explicitly makes the luminance a decision with
 * a number on it (`TRACER_TIER_LUMA_FRACTION`) instead of a side effect of which
 * rung the hue landed on.
 *
 * *Why the byte domain is right.* `three/webgpu-support.ts` sets
 * `ColorManagement.enabled = false` app-wide, so `TSL.color(hex)` hands the raw
 * sRGB bytes to the shader with no linearisation. The whole port works in
 * `BARON.PAL`'s own byte domain, so doing the arithmetic there is not an
 * approximation of a linear-light derivation — it is the domain the numbers
 * actually reach the GPU in, and it is what makes the friendly case come out
 * **bit-identical to its palette rung**: 211 renormalised to 1.0 of 211's own
 * luminance is a scale of exactly 1, so `tracerTint(TRACER_MID_FRIENDLY, 1)` is
 * `TSL.color(0xfbfb00)` to the last bit.
 *
 * *Overdrive is legal and is not clipped.* A rung whose chromaticity cannot
 * reach the target luminance inside the unit cube comes back with a channel past
 * 1 — the player's 210 needs no boost, but a hotter target would. Nothing
 * truncates it, for the same reason `TRACER_HEAD_GAIN` is allowed above 1: this
 * is a colour the material multiplies by its gain and *adds* to the frame, and
 * nothing clamps until the framebuffer.
 */
export function tracerTint(hex: number, fraction: number): readonly [number, number, number] {
  const red = (hex >> 16) & 0xff;
  const green = (hex >> 8) & 0xff;
  const blue = hex & 0xff;
  const scale = (fraction * referenceLuma()) / luma(red, green, blue);
  return [(red * scale) / 255, (green * scale) / 255, (blue * scale) / 255];
}

/** The three tints, resolved once at module load — `TracerPool.place` only ever looks them up. */
export const TRACER_TIER_TINT: Readonly<Record<TracerTier, readonly [number, number, number]>> = {
  player: tracerTint(TRACER_MID_PLAYER, TRACER_TIER_LUMA_FRACTION.player),
  friendly: tracerTint(TRACER_MID_FRIENDLY, TRACER_TIER_LUMA_FRACTION.friendly),
  hostile: tracerTint(TRACER_MID_HOSTILE, TRACER_TIER_LUMA_FRACTION.hostile),
};
